import { Trans } from '@lingui/macro'
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import {
  Box,
  Grid,
  InputAdornment,
  SvgIcon,
  TextField,
  Tooltip
} from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { baseMUITheme } from 'app/theme/themeBase'
import { useTheme } from 'app/theme/ThemeProvider'
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableProps
} from 'mui-datatables'
import React, { useState } from 'react'
import { FilterCard } from '../resources/resources-list/FilterCard'
import FilterChips from './FilterChips'

const datatableOverrides = {
  components: {
    MUIDataTable: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 0,
          boxShadow: 'none',
          gap: 0
        })
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '15px',
          lineHeight: '25px',
          fontWeight: 400,
          verticalAlign: 'middle',
          textAlign: 'left',
          padding: '16px 0px',
          '&.MuiTableCell-body': {
            verticalAlign: 'middle',
          },
          '&.MuiTableCell-head': {
            verticalAlign: 'top',
            alignItems: 'flex-start',
          },
          '&.non-sortable': {
            paddingTop: '22px',
          },
          '&:first-of-type': {
            padding: '16px 0px 16px 32px'
          },
          '&:last-of-type': {
            paddingRight: '32px'
          }
        },
        head: {
          fontSize: '15px',
          fontWeight: 400,
          lineHeight: '25px',
          textAlign: 'left',
          verticalAlign: 'top',
          padding: '16px 0px',
          backgroundColor: '#ffffff',
        }
      }
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {
          textAlign: 'left'
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          verticalAlign: 'top',
          textAlign: 'left',
        },
        fixedHeader: {
          verticalAlign: 'top',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textAlign: 'left'
        }
      }
    },
    // MuiButton: {
    //   styleOverrides: {
    //     root: {
    //       fontSize: '15px',
    //       fontWeight: 400,
    //       lineHeight: '25px',
    //       textAlign: 'left',
    //       justifyContent: 'flex-start',
    //       paddingTop: 0,
    //       '&:first-of-type': {
    //         paddingTop: 0
    //       }
    //     }
    //   }
    // },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(23, 55, 79, 0.10)',
            borderRadius: '4px',
            color: '#17374F'
          },
          "&[data-testid='Filter Table-iconButton']:hover": {
            background: 'rgba(23, 55, 79, 0.10)',
            color: '#17374F !important'
          },
          "&[data-testid='Filter Table-iconButton']": {
            // position: 'absolute',
            // right: '32px',
            // top: '-54px',
            // transform: 'translateY(-50%)'
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        },
        toolbar: {
          // overflow: 'hidden'
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        // root: {
        //   border: 'none !important',
        //   minHeight: '12px !important',
        //   height: '32px',
        //   padding: 0,
        //   '@media (min-width: 600px)': {
        //     minHeight: '12px !important'
        //   }
        // }
      }
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          lineHeight: 'inherit',
          padding: 0,
          display: 'flex',
        },
        icon: {
          width: '24px',
          height: '24px',
          fontSize: '24px',
          verticalAlign: 'middle',
          marginTop: 0,
          position: 'relative',
          marginLeft: '20px',
          top: '2px'
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '35px',
          height: '35px',
          padding: '8px 10px'
        }
      }
    }
  }
}

interface FilteredColumnData {
  key: string
  values?: string[]
}

interface CustomDataTableProps extends MUIDataTableProps {
  columns: any[]
  options: MUIDataTableOptions
  data: any[]
  filteredColumns?: FilteredColumnData[]
  title?: React.ReactNode | null
}

/**
 * A customized table component based on the MUI DataTable,which allows displaying data
 * in a table with search functionality. Additionally, the component customizes
 * the MUI theme and styles for various elements.
 *
 * @component
 *
 * @param {object} props - The component's props.
 * @param {React.ReactNode | null} [props.title=null] - Optional title for the table.
 * @param {Array} props.columns - The columns for the table, defining data and options for each column.
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {object} props.options - Configuration options for the table, such as search, filtering, etc.
 *
 * @returns {JSX.Element} - Renders the `CustomDataTable` component with the table and an optional search field.
 */
const CustomDataTable = ({
  columns,
  showBaseFilters,
  options,
  data,
  filteredColumns = [],
  title = null,
  ...props
}: CustomDataTableProps): JSX.Element => {
  const { mode } = useTheme() ?? { mode: 'light' }
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<Record<string, string[]>>({})
  const customSearch = options.search !== false

  const baseTheme = baseMUITheme(mode)
  const datatableTheme = createTheme(baseTheme, datatableOverrides)

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const getAvailableFilterValues = (key: string): string[] => {
    return Array.from(
      new Set(
        data
          .map((row) => row[key]?.toString() ?? '')
          .filter((value) => value !== '')
      )
    )
  }

  const filteredData = data.filter((row) =>
    Object.entries(filters).every(
      ([key, selectedValues]) =>
        selectedValues.length === 0 || selectedValues.includes(row[key])
    )
  )

  const customOptions = {
    ...options,
    search: customSearch ? false : options.search,
    searchText: searchTerm,
    filter: showBaseFilters || false,
    justifyContent: 'space-between',
    onSearchChange: customSearch
      ? (searchQuery: string) => setSearchTerm(searchQuery)
      : options.onSearchChange ?? (() => {})
  }

  const usedValuesMap = Object.fromEntries(
    filteredColumns.map(filter => {
      const uniqueValues = new Set(
        data
          .map(item => item[filter.key])
          .filter(value => value != null)
      )
      return [filter.key, Array.from(uniqueValues)]
    })
  )  

  const filtersForChips = Object.fromEntries(filteredColumns?.map(({ key, values }) => {
    const filterValues = (values ?? getAvailableFilterValues(key)).map(v => 
      typeof v === 'string' ? { key: v, label: v } : v
    )
  
    const filtersArray = filterValues
      .map(value => ({
        value: value.key,
        label: value.label,
        isChecked: filters[key]?.includes(value.key) ?? false
      }))
      .filter(obj => obj.isChecked)
  
    return [key, filtersArray]
  }) ?? [])  

  return (
    <ThemeProvider theme={datatableTheme}>
      <Box sx={{ border: '1px solid #17374F', backgroundColor: '#ffffff' }}>
        {(title && filteredColumns.length > 0) && (
          <Box
            display='flex'
            alignItems='flex-start'
            justifyContent='flex-start'
            sx={{ padding: '16px 32px 0px 32px' }}
          >
            {title}
          </Box>
        )}
        <Box
          display='flex'
          alignItems='flex-start'
          justifyContent='space-between'
          sx={{ padding: '16px 32px 0px 32px' }}
        >
          <Grid
            container
            item
            alignItems='flex-start'
            justifyContent='flex-start'
            gap='24px'
          >   
            {filteredColumns.length > 0
              ? (
                  filteredColumns.map(({ key, values }) => {
                    const filterValues = (values != null)
                      ? values.map((v) =>
                        typeof v === 'string' ? { key: v, label: v } : v
                      ).filter(
                        obj => usedValuesMap[key]?.includes(obj.key)
                      )
                      : getAvailableFilterValues(key).map((v) => ({
                        key: v,
                        label: v
                      }))

                    return (
                      <Box key={key}>
                        <FilterCard
                          sx={{ width: '207px' }}
                          filters={filterValues.map((value) => ({
                            key: value.key,
                            label: value.label,
                            isChecked: filters[key]?.includes(value.key) ?? false
                          }))}
                          label={
                            <Trans>
                              {columns.find((col) => col.name === key)?.label || key}
                            </Trans>
                    }
                          onFilterChange={(newFilters) => {
                            setFilters((prev) => ({
                              ...prev,
                              [key]: newFilters
                                .filter((f) => f.isChecked)
                                .map((f) => f.key)
                            }))
                          }}
                        />
                      </Box>
                    )
                  })
                )
              : (
                  title
                )}
          </Grid>

          {customSearch && (
            <TextField
              value={searchTerm}
              onChange={handleSearchChange}
              variant='outlined'
              sx={{
                display: 'flex',
                width: '405px',
                height: '42px',
                '& fieldset': { border: '1px solid rgba(0, 0, 0, 0.40)' },
                '& .MuiInputBase-root': {
                  background: '#FFF',
                  height: '42px'
                },
                '& .MuiInputBase-input': {
                  padding: '10px 14px'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    {searchTerm !== ''
                      ? (
                        <SvgIcon
                          component={SearchOffOutlinedIcon}
                          sx={{ width: 28, height: 28, cursor: 'pointer' }}
                          onClick={() => setSearchTerm('')}
                        />
                        )
                      : (
                        <Tooltip title='Search'>
                          <SvgIcon
                            component={SearchOutlinedIcon}
                            sx={{ width: 28, height: 28 }}
                          />
                        </Tooltip>
                        )}
                  </InputAdornment>
                )
              }}
            />
          )}
        </Box>

        {filteredColumns.length > 0 && (
          <Box sx={{ marginLeft: '32px' }}>
            <FilterChips
              filters={filtersForChips}
              onRemoveFilter={(columnKey, filterValue) =>
                setFilters((prev) => {
                  const updatedFilters = { ...prev }
                  updatedFilters[columnKey] = updatedFilters[columnKey]?.filter(
                    (value) => value !== filterValue
                  )
                  if (updatedFilters[columnKey].length === 0) {
                    delete updatedFilters[columnKey]
                  }
                  return updatedFilters
                })}
              onClearAllFilters={() => setFilters({})}
            />
          </Box>
        )}
        <MUIDataTable
          columns={columns}
          options={customOptions}
          data={filteredData}
          {...props}
        />
      </Box>
    </ThemeProvider>
  )
}

export default CustomDataTable
